import type { ChecklistItem, ChecklistOut } from '@/api/onboardingChecklist';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { checklistStatsLine } from '@/lib/onboardingChecklist';

/** Shared presentational checklist — the portal and RECON render the
 *  identical server-derived payload through this one component. Items
 *  are source-agnostic (today all `derived`; future practitioner
 *  request items land in the same list without rework). */
export function OnboardingChecklist({
  data,
  title,
  description,
}: {
  data: ChecklistOut;
  title: string;
  description?: string;
}) {
  const done = data.items.filter((i) => i.done).length;
  return (
    <Card>
      <CardHeader>
        <div className="flex flex-wrap items-baseline justify-between gap-2">
          <CardTitle>{title}</CardTitle>
          <span className="text-xs font-medium text-brand-slate">
            {done}/{data.items.length} complete
          </span>
        </div>
        <CardDescription>
          {checklistStatsLine(data.stats)}
          {description ? ` — ${description}` : ''}
        </CardDescription>
      </CardHeader>
      <CardContent>
        <ul className="grid gap-1.5">
          {data.items.map((item) => (
            <ChecklistRow key={item.key} item={item} />
          ))}
        </ul>
      </CardContent>
    </Card>
  );
}

function ChecklistRow({ item }: { item: ChecklistItem }) {
  return (
    <li className="flex items-center gap-2 text-sm">
      <span
        aria-hidden
        className={`flex h-5 w-5 shrink-0 items-center justify-center rounded-full border text-[11px] ${
          item.done
            ? 'border-emerald-600 bg-emerald-600 text-white'
            : 'border-border bg-white text-transparent'
        }`}
      >
        ✓
      </span>
      <span className={item.done ? 'text-brand-navy' : 'text-brand-slate'}>
        {item.label}
        {typeof item.count === 'number' && item.count > 0 && (
          <span className="text-brand-slate"> ({item.count})</span>
        )}
      </span>
      <span className="sr-only">{item.done ? 'done' : 'not done'}</span>
    </li>
  );
}
